// Learning Coach — per-word scheduling (LB-280, half one).
//
// Alan, 14 Sep: "when a word has been successfully answered twice, consider it mastered and
// show it less frequently, instead favour words which have been answered incorrectly, or
// correctly fewer times."
//
// Today spelling has no per-word memory at all: every word in a band stays equally likely
// forever, however many times it has been right. Vocabulary solved this before Coach existed
// (vocabBank.correctCount, mastered at 2, back on a miss) and this brings the same idea to the
// Coach draw for both.
//
// ── How this works without editing the engine ───────────────────────────────────────────
// src/lib/adaptive.js is Fable's and is not modified. It shuffles within each grade tier, so
// ordering the items before handing them over would not survive. The only lever that does is
// MEMBERSHIP: a word that is not in the pool cannot be drawn. So schedulePool() hands the
// engine a smaller pool — everything unseen, most of what has been right once, and a thin
// sample of what is solid — and the engine then does its own grade targeting on that.
//
// What the engine already does, and is deliberately NOT duplicated here: words the child got
// WRONG are in its own practice set and take the first ~20% of every draw. "Favour words
// answered incorrectly" is already true; this file only adds the "correctly fewer times" half.
//
// MASTERED means two correct answers COLD, on DIFFERENT days. Cold is first attempt — spelling's
// retry round has been practice-only since LB-278, so a retry correct never reaches here. Twice
// in one sitting is recognition, not recall: the second answer is a minute after the first, so
// the day is what makes "cold" mean anything. A later miss resets the count to zero.
//
// Only ENGINE-drawn answers count. A word answered right on a school list was practised that
// week, so letting it retire a word would hide from Coach exactly the words a child has been
// coached on. This is a one-line change (drop the `fromEngine` guard in recordWordResults) if
// that turns out to be the wrong call.
export const WORD_MASTERED_AT = 2;

// How much of the pool is held back for words the child has nearly finished with. Roughly
// mirrors the engine's own retention share, so a solid word resurfaces occasionally rather
// than never — retention is the point of testing at all.
const SEEN_ONCE_SHARE = 0.5;
const SOLID_SHARE = 0.12;

const keyOf = (it) => String((it && (it.id ?? it.word ?? it.key)) ?? "");
const countOf = (words, key) => ((words || {})[key] || {}).c || 0;

export function wordCounts(slice, game) {
  return ((slice || {})[game] || {});
}

// results: the rows a test reports. fromEngine: was this a Coach draw (true) or the parent's
// own list (false)? Returns the updated per-word map for that game.
export function recordWordResults(wordsIn, results, { day, fromEngine = true } = {}) {
  if (!fromEngine) return wordsIn || {};
  const words = { ...(wordsIn || {}) };
  for (const r of Array.isArray(results) ? results : []) {
    const k = keyOf(r);
    if (!k) continue;
    const prev = words[k] || { c: 0, d: null };
    if (!r.correct) words[k] = { c: 0, d: prev.d };          // a miss puts it back in rotation
    else if (prev.d !== day) words[k] = { c: (prev.c || 0) + 1, d: day }; // one credit a day
  }
  return words;
}

export function masteredWordCount(words) {
  return Object.values(words || {}).filter((w) => (w.c || 0) >= WORD_MASTERED_AT).length;
}

// A deterministic 0..1 from a key plus the day, so the sampling is stable WITHIN a day — a
// child who abandons a test and starts again gets the same pool rather than a reshuffled one —
// and moves on naturally between days. Math.random() here would reshuffle mid-session.
function stableUnit(key, salt) {
  let h = 2166136261;
  const s = `${key}|${salt}`;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return ((h >>> 0) % 10000) / 10000;
}

// The pool handed to adaptiveDraw. Never returns fewer than `size` items where the full pool
// could supply them: a scheduling preference must not starve a test.
export function schedulePool(items, words, { size = 15, day = "" } = {}) {
  const pool = (items || []).filter(Boolean);
  if (pool.length <= size) return pool;
  const unseen = [], once = [], solid = [];
  for (const it of pool) {
    const c = countOf(words, keyOf(it));
    (c === 0 ? unseen : c < WORD_MASTERED_AT ? once : solid).push(it);
  }
  const sample = (arr, share) => arr.filter((it) => stableUnit(keyOf(it), day) < share);
  let out = [...unseen, ...sample(once, SEEN_ONCE_SHARE), ...sample(solid, SOLID_SHARE)];
  // Top up in the order the child needs least, so the engine always has enough to draw from
  // and to keep its own grade distribution meaningful.
  if (out.length < size) {
    const have = new Set(out.map(keyOf));
    for (const it of [...once, ...solid]) { if (out.length >= size * 2) break; if (!have.has(keyOf(it))) { have.add(keyOf(it)); out.push(it); } }
  }
  return out.length ? out : pool;
}

// Words per Coach test. Free-format (Alan, 14 Sep) — the parent types a number rather than
// picking from a list — so it is clamped here rather than trusted.
export const MIN_TEST_SIZE = 4;
export const MAX_TEST_SIZE = 40;
export function clampTestSize(n, fallback) {
  const v = parseInt(n, 10);
  if (!Number.isFinite(v)) return fallback;
  return Math.max(MIN_TEST_SIZE, Math.min(MAX_TEST_SIZE, v));
}
